import { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Gift, Loader2, Search, Trash2, Plus, ShieldCheck, AlertTriangle, FileText, Megaphone } from "lucide-react";
import { CATEGORY_LABELS, formatBytes } from "../lib/documents";

export default function WelcomeDocsPage() {
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState("");
    const [results, setResults] = useState(null);
    const [searching, setSearching] = useState(false);
    const [busyId, setBusyId] = useState(null);
    const [featured, setFeatured] = useState(null);
    const [featQuery, setFeatQuery] = useState("");
    const [featResults, setFeatResults] = useState(null);
    const [featSearching, setFeatSearching] = useState(false);

    const doFeatSearch = async (e) => {
        e?.preventDefault();
        if (!featQuery.trim()) return;
        setFeatSearching(true);
        try {
            const { data } = await api.get("/welcome-docs/search", { params: { q: featQuery } });
            setFeatResults(data);
        } catch {
            toast.error("Errore nella ricerca");
        } finally {
            setFeatSearching(false);
        }
    };

    const fetchFeatured = useCallback(async () => {
        try {
            const { data } = await api.get("/welcome-docs/featured");
            setFeatured(data);
        } catch (e) {
            console.warn("Caricamento documento in evidenza non riuscito", e);
        }
    }, []);

    const setFeaturedDoc = async (doc) => {
        setBusyId(`feat-${doc.document_id}`);
        try {
            await api.put("/welcome-docs/featured", { source_document_id: doc.document_id });
            toast.success(`"${doc.title || doc.filename}" ora in evidenza per tutti i dipendenti`);
            setFeatQuery("");
            setFeatResults(null);
            await fetchFeatured();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore");
        } finally {
            setBusyId(null);
        }
    };

    const clearFeatured = async () => {
        if (!window.confirm("Rimuovere il documento in evidenza dalla home dei dipendenti?")) return;
        try {
            await api.delete("/welcome-docs/featured");
            setFeatured({ entry: null, source: null });
            toast.success("Documento in evidenza rimosso");
        } catch {
            toast.error("Errore nella rimozione");
        }
    };

    const fetchEntries = useCallback(async () => {
        try {
            const { data } = await api.get("/welcome-docs");
            setEntries(data);
        } catch (e) {
            toast.error("Errore nel caricamento della lista");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchEntries();
        fetchFeatured();
    }, [fetchEntries, fetchFeatured]);

    const doSearch = async (e) => {
        e?.preventDefault();
        setSearching(true);
        try {
            const { data } = await api.get("/welcome-docs/search", { params: { q: query } });
            setResults(data);
        } catch (err) {
            toast.error("Errore nella ricerca");
        } finally {
            setSearching(false);
        }
    };

    const addEntry = async (doc) => {
        setBusyId(doc.document_id);
        try {
            await api.post("/welcome-docs", { source_document_id: doc.document_id });
            toast.success(`"${doc.title || doc.filename}" aggiunto ai documenti di benvenuto`);
            await fetchEntries();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore nell'aggiunta");
        } finally {
            setBusyId(null);
        }
    };

    const toggleEntry = async (entry, active) => {
        setEntries((prev) => prev.map((x) => (x.id === entry.id ? { ...x, active } : x)));
        try {
            await api.patch(`/welcome-docs/${entry.id}`, { active });
        } catch (e) {
            setEntries((prev) => prev.map((x) => (x.id === entry.id ? { ...x, active: !active } : x)));
            toast.error("Errore nel salvataggio");
        }
    };

    const deleteEntry = async (entry) => {
        if (!window.confirm(`Rimuovere "${entry.label}" dalla lista? I documenti già assegnati NON verranno eliminati.`)) return;
        setBusyId(entry.id);
        try {
            await api.delete(`/welcome-docs/${entry.id}`);
            setEntries((prev) => prev.filter((x) => x.id !== entry.id));
            toast.success("Rimosso dalla lista");
        } catch (e) {
            toast.error("Errore nella rimozione");
        } finally {
            setBusyId(null);
        }
    };

    const configuredIds = new Set(entries.map((x) => x.source_document_id));

    return (
        <div className="mx-auto max-w-5xl px-6 py-8" data-testid="welcome-docs-page">
            <div className="mb-6 flex items-start gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-md bg-[#376DB3]/10 text-[#376DB3]">
                    <Gift className="h-6 w-6" />
                </div>
                <div>
                    <h1 className="font-display text-2xl font-bold text-slate-800">Documenti di benvenuto</h1>
                    <p className="text-sm text-slate-500">
                        Questi file vengono assegnati automaticamente a tutti i dipendenti attivi e al fascicolo di ogni nuovo dipendente al momento della creazione.
                    </p>
                </div>
            </div>

            {/* Featured document for all employees */}
            <div className="mb-8 rounded-lg border border-amber-200 bg-white shadow-sm" data-testid="featured-doc-section">
                <div className="flex items-center gap-2 border-b border-amber-100 bg-amber-50/60 px-5 py-3 text-xs font-semibold uppercase tracking-wide text-amber-700">
                    <Megaphone className="h-4 w-4" />
                    Documento in evidenza per tutti i dipendenti
                </div>
                {featured?.entry ? (
                    <div className="flex flex-wrap items-center gap-3 px-5 py-3" data-testid="featured-doc-current">
                        <FileText className="h-4 w-4 flex-shrink-0 text-amber-500" />
                        <div className="min-w-0 flex-1">
                            <div className="truncate text-sm font-medium text-slate-800">{featured.entry.label}</div>
                            <div className="mt-0.5 flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
                                {featured.source ? (
                                    <>
                                        <span className="truncate">{featured.source.filename}</span>
                                        <span>·</span>
                                        <span>{CATEGORY_LABELS[featured.source.category] || featured.source.category}</span>
                                        <span>·</span>
                                        <span>{formatBytes(featured.source.size_bytes)}</span>
                                    </>
                                ) : (
                                    <span className="flex items-center gap-1 text-red-600">
                                        <AlertTriangle className="h-3 w-3" />
                                        File sorgente non più disponibile — rimuovi questa voce
                                    </span>
                                )}
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={clearFeatured}
                            className="flex h-8 w-8 items-center justify-center rounded text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600"
                            title="Rimuovi dall'evidenza"
                            data-testid="featured-doc-remove-btn"
                        >
                            <Trash2 className="h-4 w-4" />
                        </button>
                    </div>
                ) : (
                    <div className="px-5 py-4" data-testid="featured-doc-empty">
                        <p className="text-sm text-slate-500">
                            Nessun documento in evidenza. Cerca qui il file da mostrare in cima alla home di
                            tutti i dipendenti che lo hanno nel fascicolo:
                        </p>
                        <form onSubmit={doFeatSearch} className="mt-3 flex gap-2">
                            <div className="relative flex-1">
                                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                                <Input
                                    value={featQuery}
                                    onChange={(e) => setFeatQuery(e.target.value)}
                                    placeholder="Cerca per nome file o titolo (es. comunicazione, regolamento...)"
                                    className="pl-9"
                                    data-testid="featured-search-input"
                                />
                            </div>
                            <Button
                                type="submit"
                                disabled={featSearching || !featQuery.trim()}
                                className="gap-2 bg-amber-600 text-white hover:bg-amber-700"
                                data-testid="featured-search-btn"
                            >
                                {featSearching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                                Cerca
                            </Button>
                        </form>
                        {featResults !== null && (
                            <div className="mt-3 max-h-64 overflow-y-auto rounded-md border border-slate-200" data-testid="featured-search-results">
                                {featResults.length === 0 ? (
                                    <p className="px-4 py-3 text-sm text-slate-500">Nessun documento trovato: prova con un'altra parola.</p>
                                ) : (
                                    featResults.map((doc) => (
                                        <div
                                            key={doc.document_id}
                                            className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-3 py-2 last:border-b-0"
                                        >
                                            <FileText className="h-4 w-4 flex-shrink-0 text-slate-400" />
                                            <div className="min-w-0 flex-1">
                                                <div className="truncate text-sm font-medium text-slate-800">{doc.title || doc.filename}</div>
                                                <div className="text-[11px] text-slate-500">
                                                    {doc.filename} · {CATEGORY_LABELS[doc.category] || doc.category} · {formatBytes(doc.size_bytes)} · presente in {doc.copies} {doc.copies === 1 ? "fascicolo" : "fascicoli"}
                                                </div>
                                            </div>
                                            <Button
                                                size="sm"
                                                disabled={busyId === `feat-${doc.document_id}`}
                                                onClick={() => setFeaturedDoc(doc)}
                                                className="gap-1.5 bg-amber-600 text-white hover:bg-amber-700"
                                                data-testid={`featured-pick-btn-${doc.document_id}`}
                                            >
                                                {busyId === `feat-${doc.document_id}` ? (
                                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                                ) : (
                                                    <Megaphone className="h-3.5 w-3.5" />
                                                )}
                                                Metti in evidenza
                                            </Button>
                                        </div>
                                    ))
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Configured list */}
            <div className="mb-8 rounded-lg border border-slate-200 bg-white shadow-sm" data-testid="welcome-docs-list">
                <div className="border-b border-slate-100 px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    File assegnati ai nuovi assunti ({entries.length})
                </div>
                {loading ? (
                    <div className="flex items-center justify-center py-10 text-slate-400">
                        <Loader2 className="h-5 w-5 animate-spin" />
                    </div>
                ) : entries.length === 0 ? (
                    <div className="px-5 py-8 text-center text-sm text-slate-500">
                        Nessun documento configurato. Cerca un file qui sotto e aggiungilo alla lista.
                    </div>
                ) : (
                    <ul className="divide-y divide-slate-100">
                        {entries.map((entry) => (
                            <li key={entry.id} className="flex flex-wrap items-center gap-3 px-5 py-3" data-testid={`welcome-doc-row-${entry.id}`}>
                                <FileText className="h-4 w-4 flex-shrink-0 text-slate-400" />
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2 truncate text-sm font-medium text-slate-800">
                                        <span className="truncate">{entry.label}</span>
                                        {entry.only_timbratura_enabled && (
                                            <Badge className="bg-sky-100 text-[10px] font-bold text-sky-700 hover:bg-sky-100" data-testid={`welcome-doc-timbratura-badge-${entry.id}`}>
                                                SOLO ABILITATI TIMBRATURA
                                            </Badge>
                                        )}
                                    </div>
                                    <div className="mt-0.5 flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
                                        {entry.source ? (
                                            <>
                                                <span className="truncate">{entry.source.filename}</span>
                                                <span>·</span>
                                                <span>{CATEGORY_LABELS[entry.source.category] || entry.source.category}</span>
                                                <span>·</span>
                                                <span>{formatBytes(entry.source.size_bytes)}</span>
                                                {entry.source.requires_acknowledgment && (
                                                    <Badge variant="outline" className="gap-1 border-amber-300 bg-amber-50 px-1.5 py-0 text-[10px] text-amber-700">
                                                        <ShieldCheck className="h-2.5 w-2.5" />
                                                        Presa visione
                                                    </Badge>
                                                )}
                                            </>
                                        ) : (
                                            <span className="flex items-center gap-1 text-red-600">
                                                <AlertTriangle className="h-3 w-3" />
                                                File sorgente non più disponibile — rimuovi questa voce
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="flex flex-shrink-0 items-center gap-3">
                                    <label className="flex items-center gap-2 text-xs text-slate-500">
                                        {entry.active ? "Attivo" : "Sospeso"}
                                        <Switch
                                            checked={!!entry.active}
                                            onCheckedChange={(v) => toggleEntry(entry, v)}
                                            data-testid={`welcome-doc-toggle-${entry.id}`}
                                        />
                                    </label>
                                    <button
                                        type="button"
                                        onClick={() => deleteEntry(entry)}
                                        disabled={busyId === entry.id}
                                        className="flex h-8 w-8 items-center justify-center rounded text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600"
                                        title="Rimuovi dalla lista"
                                        data-testid={`welcome-doc-delete-${entry.id}`}
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </button>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {/* Add from archive */}
            <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm" data-testid="welcome-docs-add-section">
                <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Aggiungi un file dall'archivio
                </div>
                <p className="mb-3 text-xs text-slate-400">
                    Cerca per nome file o titolo tra i documenti già presenti nell'archivio (i file identici sono raggruppati).
                </p>
                <form onSubmit={doSearch} className="mb-4 flex gap-2">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                        <Input
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Es. guida, informativa, regolamento…"
                            className="pl-9"
                            data-testid="welcome-docs-search-input"
                        />
                    </div>
                    <Button type="submit" disabled={searching} className="gap-2 bg-[#376DB3] text-white hover:bg-[#376DB3]/90" data-testid="welcome-docs-search-btn">
                        {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                        Cerca
                    </Button>
                </form>
                {results && (
                    results.length === 0 ? (
                        <div className="py-4 text-center text-sm text-slate-500">Nessun file trovato.</div>
                    ) : (
                        <ul className="divide-y divide-slate-100 rounded-md border border-slate-100">
                            {results.map((doc) => {
                                const already = configuredIds.has(doc.document_id);
                                return (
                                    <li key={doc.document_id} className="flex flex-wrap items-center gap-3 px-4 py-2.5" data-testid={`welcome-search-result-${doc.document_id}`}>
                                        <div className="min-w-0 flex-1">
                                            <div className="truncate text-sm text-slate-800">{doc.title || doc.filename}</div>
                                            <div className="text-[11px] text-slate-500">
                                                {doc.filename} · {CATEGORY_LABELS[doc.category] || doc.category} · {formatBytes(doc.size_bytes)}
                                                {doc.copies > 1 && ` · presente in ${doc.copies} fascicoli`}
                                            </div>
                                        </div>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            disabled={busyId === `feat-${doc.document_id}` || featured?.entry?.source_document_id === doc.document_id}
                                            onClick={() => setFeaturedDoc(doc)}
                                            className="gap-1.5 border-amber-300 text-amber-700 hover:bg-amber-50"
                                            data-testid={`featured-set-btn-${doc.document_id}`}
                                        >
                                            {busyId === `feat-${doc.document_id}` ? (
                                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                            ) : (
                                                <Megaphone className="h-3.5 w-3.5" />
                                            )}
                                            {featured?.entry?.source_document_id === doc.document_id ? "In evidenza" : "In evidenza"}
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant={already ? "outline" : "default"}
                                            disabled={already || busyId === doc.document_id}
                                            onClick={() => addEntry(doc)}
                                            className={already ? "gap-1.5" : "gap-1.5 bg-[#376DB3] text-white hover:bg-[#376DB3]/90"}
                                            data-testid={`welcome-add-btn-${doc.document_id}`}
                                        >
                                            {busyId === doc.document_id ? (
                                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                            ) : (
                                                <Plus className="h-3.5 w-3.5" />
                                            )}
                                            {already ? "Già in lista" : "Aggiungi"}
                                        </Button>
                                    </li>
                                );
                            })}
                        </ul>
                    )
                )}
            </div>
        </div>
    );
}
